import React, { useMemo, useState } from 'react';
import { Chip, Input } from '@heroui/react';
import { Typography } from '../../../../components';
import {
  SearchInputIc,
  OnlyFansIc,
  InstagramIc,
  GoogleIc,
  TwitterIc,
  GlobalIc,
  IcReddit,
  IcAnotherSoc,
  IcTg,
  IcTikTok,
  IcYoutube,
} from '../../../../components/icons';

interface ProhibitedSourcesTooltipProps {
  sources: string[];
  offerShortId: string;
  modelUsername: string;
}

const SOURCE_ICONS: Record<string, React.ReactNode> = {
  onlyfans: <OnlyFansIc size={16} fill="#A8A8B0" />,
  only_fans: <OnlyFansIc size={16} fill="#A8A8B0" />,
  instagram: <InstagramIc size={16} fill="#A8A8B0" />,
  google: <GoogleIc size={16} fill="#A8A8B0" />,
  twitter: <TwitterIc size={16} fill="#A8A8B0" />,
  reddit: <IcReddit size={16} />,
  telegram: <IcTg size={16} />,
  tiktok: <IcTikTok size={16} />,
  youtube: <IcYoutube size={16} />,
  other: <IcAnotherSoc size={16} />,
  another: <IcAnotherSoc size={16} />,
};

const SOURCE_LABELS: Record<string, string> = {
  onlyfans: 'OnlyFans',
  only_fans: 'OnlyFans',
  instagram: 'Instagram',
  google: 'Google',
  twitter: 'Twitter',
  reddit: 'Reddit',
  telegram: 'Telegram',
  tiktok: 'TikTok',
  youtube: 'YouTube',
  other: 'Other',
  another: 'Other',
};

const ProhibitedSourcesTooltip: React.FC<ProhibitedSourcesTooltipProps> = ({
  sources,
  offerShortId,
  modelUsername,
}) => {
  const [searchQuery, setSearchQuery] = useState('');

  const normalizedSources = useMemo(() => {
    return sources.map((source) => {
      const key = source.toLowerCase();
      return {
        key,
        label: SOURCE_LABELS[key] || source.charAt(0).toUpperCase() + source.slice(1),
        icon: SOURCE_ICONS[key] || <GlobalIc size={16} />,
      };
    }).sort((a, b) => a.label.localeCompare(b.label));
  }, [sources]);

  const filteredSources = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return normalizedSources;

    return normalizedSources.filter((source) =>
      source.label.toLowerCase().includes(query) ||
      source.key.toLowerCase().includes(query)
    );
  }, [normalizedSources, searchQuery]);

  return (
    <div className="flex w-[400px] max-w-[400px] flex-col gap-4 p-4">
      <div className="flex items-start justify-between">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-[8px]">
            <Typography variant="h6" className="text-[#11181C] text-[16px] leading-[28px] font-medium">
              Запрещённые источники
            </Typography>
            <Chip
              classNames={{
                base: 'bg-[#F4F4F5] h-[24px] rounded-lg',
                content: 'text-sm font-medium !text-[#71717A]',
              }}
            >
              {sources.length}
            </Chip>
          </div>
          <div className="flex items-center gap-2">
            <Typography variant="body2" className="text-[#11181C] font-medium">
              {offerShortId}
            </Typography>
            <a
              href={`https://onlyfans.com/${modelUsername}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#006FEE] text-sm font-medium hover:underline"
            >
              @{modelUsername}
            </a>
          </div>
        </div>
      </div>

      {sources.length > 5 && (
        <div className="flex flex-col gap-[8px]">
          <Input
            type="text"
            placeholder="Поиск источника..."
            size="sm"
            value={searchQuery}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
            endContent={<SearchInputIc className="text-[#A1A1AA]" />}
            classNames={{
              base: 'w-full',
              input: 'text-sm text-[#11181C] placeholder:text-[#A1A1AA]',
              inputWrapper: 'rounded-xl h-[36px] bg-[#F4F4F5] border border-transparent',
            }}
          />
        </div>
      )}

      <div className="flex max-h-[240px] flex-col gap-3 overflow-y-auto pr-1">
        {filteredSources.map((source) => (
          <div
            key={source.key}
            className="flex items-center gap-3 h-[24px] rounded-lg"
          >
            <div className="w-[20px] h-[20px] flex items-center justify-center">
              {source.icon}
            </div>
            <Typography variant="body2" className="text-[#11181C]">
              {source.label}
            </Typography>
          </div>
        ))}

        {filteredSources.length === 0 && (
          <div className="rounded-lg border border-[#E4E4E7] px-3 py-4 text-center">
            <Typography variant="body2" className="text-[#71717A]">
              Источники не найдены
            </Typography>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProhibitedSourcesTooltip;
